<template>
  <HeadlessComboBox
    v-model="comboBoxModel"
    @update:modelValue="onUpdate"
  >
    <div class="relative w-full">
      <div class="relative input-control">
        <ComboboxInput
          @change="onQueryChange"
          @focus="onQueryFocus"
          :displayValue="label"
        />
        <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDownIcon
            v-if="!searchMode"
            class="h-5 w-5 text-gray-600"
            aria-hidden="true"
          />
        </ComboboxButton>
      </div>

      <TransitionRoot
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        @after-leave="query = ''"
      >
        <ComboboxOptions
          v-if="filteredData"
          class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-100 py-1 text-base shadow-lg ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
        >
          <div
            v-if="filteredData.length === 0 && query !== ''"
            class="relative cursor-default select-none py-2 px-4 text-gray-700"
          >
            Nothing found.
          </div>

          <ComboboxOption
            v-for="item in filteredData"
            :key="item.id"
            :value="item"
            as="template"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                'relative cursor-default select-none py-2 pl-8 pr-4',
                active ? 'bg-blue-600 text-gray-100' : 'text-gray-900'
              ]"
            >
              <span :class="['block truncate', selected && 'font-semibold']">
                {{ label(item) }}
              </span>
              <span
                v-if="selected"
                :class="[
                  'absolute inset-y-0 left-0 flex items-center pl-1.5',
                  active ? 'text-gray-100' : 'text-blue-600'
                ]"
              >
                <CheckIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </HeadlessComboBox>
</template>
<script setup>
import { ref, computed, watchEffect } from 'vue'

import {
  Combobox as HeadlessComboBox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
  TransitionRoot
} from '@headlessui/vue'

import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/solid'

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  filterOn: {
    type: String,
    required: true
  },
  label: {
    type: Function,
    required: true
  },
  currentSelection: {
    type: Object,
    default: null
  }
})

const emits = defineEmits(['update'])

const searchMode = computed(() => {
  return props.data.length > 500
})

const query = ref('')

const onQueryChange = _.debounce(function (e) {
  query.value = e.target.value
}, 200)

const onQueryFocus = (e) => {
  if (searchMode.value && !comboBoxModel.value) {
    e.target.value = ''
  }
}

const comboBoxModel = ref(props.currentSelection)

watchEffect(() => {
  comboBoxModel.value = props.currentSelection
})

const filteredData = computed(() => {
  if (searchMode.value && query.value.length < 3) {
    return null
  }

  return query.value === ''
    ? props.data
    : props.data.filter((item) => {
        return item[props.filterOn].toLowerCase().includes(query.value.toLowerCase())
      })
})

const onUpdate = (value) => emits('update', value)
</script>
